import React, {useEffect, useRef, useState} from 'react'
import {isWithinInterval} from 'date-fns'
import {observer} from 'mobx-react-lite'
import {Box} from '../../../vanilla'
import {Link} from '../../link'
import {SlotComponent} from '../../../types/ui'
import {
  DaysAvailable,
  DeliveryBanner as DeliveryBannerType,
  DeliveryOption
} from '../../../types/cms/delivery-banner'
import {LiveTextBanner} from '../live-text-banner'
import {CmsImage} from '../cms-image'
import {useSelectPromotionTracking} from '../../../analytics/promotion-tracking'
import {createPromoForSelectPromotion} from '../../../analytics/utils'
import {ContentParams} from '../../../types/store/content'
import {useBasketStore, useContentStore, useRapidDeliveryStore} from '../../../store/hooks/useStore'
import {DateUtils} from '../../../utils/date-utils'
import {Spinner} from '../../spinner'
import * as styles from './styles.css'

const isDeliveryTypeAvailable = (
  deliveryOption: DeliveryOption,
  params?: ContentParams
): boolean => {
  if (deliveryOption.deliveryType === 'None' || !deliveryOption.deliveryOptions) {
    return false
  }

  const currentDate =
    params?.isContentPreview && params?.previewTimestamp
      ? new Date(params.previewTimestamp)
      : new Date()
  const {daysAvailable, timesAvailableBetween} = deliveryOption.deliveryOptions
  const currentDay = DateUtils.format(currentDate, 'EEEE').toLowerCase() as keyof Omit<
    DaysAvailable,
    'restrictDays'
  >
  const isAvailableToday = !daysAvailable?.restrictDays || daysAvailable[currentDay] === true

  if (!isAvailableToday) {
    return false
  }

  const {restrictTime, startTime, endTime} = timesAvailableBetween

  if (!restrictTime) {
    return true
  }

  if (!startTime || !endTime) {
    return false
  }

  const currentDateStr = DateUtils.format(currentDate, 'yyyy-MM-dd')

  return isWithinInterval(currentDate, {
    start: new Date(`${currentDateStr}T${startTime}`),
    end: new Date(`${currentDateStr}T${endTime}`),
  })
}

export const DeliveryBanner: SlotComponent<DeliveryBannerType> = observer(({
  content,
  slotGroupName,
  slotIndex,
}) => {
  const sendSelectPromotion = useSelectPromotionTracking()
  const {params} = useContentStore()
  const {deliveryPostalCode} = useBasketStore()
  const {
    isRapidDeliveryAvailableForPostcode,
    rapidDeliveryAvailabilityResolved,
  } = useRapidDeliveryStore()
  const [loadedAssetKey, setLoadedAssetKey] = useState<string | undefined>()
  const assetImageRef = useRef<HTMLImageElement>(null)

  const deliveryTypes: DeliveryOption[] = [
    content.deliveryType1,
    content.deliveryType2,
    content.deliveryType3,
    content.deliveryType4,
    content.deliveryType5,
    content.deliveryType6,
  ].filter((dt): dt is DeliveryOption => !!dt)
  const hasRapidDeliveryOption = deliveryTypes.some(
    (deliveryType) => deliveryType.deliveryType === 'Rapid'
  )
  const isRapidAvailabilityLoading =
    !params?.isContentVisualization &&
    Boolean(deliveryPostalCode) &&
    hasRapidDeliveryOption &&
    !rapidDeliveryAvailabilityResolved

  const availableDeliveryTypes = deliveryTypes.filter((deliveryType) => {
    if (
      deliveryType.deliveryType === 'Rapid' &&
      !params?.isContentVisualization &&
      !isRapidAvailabilityLoading &&
      !isRapidDeliveryAvailableForPostcode
    ) {
      return false
    }

    return isDeliveryTypeAvailable(deliveryType, params)
  })

  const selectedDeliveryType = [...availableDeliveryTypes].sort(
    (a, b) => (a.deliveryOptions?.rank || 0) - (b.deliveryOptions?.rank || 0)
  )[0]

  const selectedBannerBackground = selectedDeliveryType?.deliveryOptions?.bannerBackground
  const selectedAsset = selectedBannerBackground?.useImage
    ? selectedBannerBackground.backgroundImage?.images?.[0]
    : selectedBannerBackground?.logo?.images?.[0]
  const selectedAssetKey = selectedAsset?.image?.id
  const isAssetLoaded = !selectedAssetKey || loadedAssetKey === selectedAssetKey
  const isBannerLoading = isRapidAvailabilityLoading || !isAssetLoaded

  useEffect(() => {
    if (selectedAssetKey && assetImageRef.current?.complete) {
      setLoadedAssetKey(selectedAssetKey)
    }
  }, [selectedAssetKey])

  if (!selectedDeliveryType?.deliveryOptions) {
    return null
  }

  const selectPromoInfo = {
    content,
    slotGroupName,
    slotPosition: slotIndex,
  }
  const deliveryOptions = selectedDeliveryType.deliveryOptions
  const {bannerBackground, offsetTitle} = deliveryOptions
  const {useImage = false, bannerColor, backgroundImage, logo} = bannerBackground || {}
  const promotion = {
    ...createPromoForSelectPromotion(selectPromoInfo),
    promotion_id: deliveryOptions.url || 'delivery-banner without link',
  }
  const offsetValue = Number(offsetTitle?.offsetValue)
  const titleOffset =
    offsetTitle?.offset && Number.isFinite(offsetValue) && offsetValue >= 0
      ? `${offsetValue}px`
      : '0px'

  const resolvedBannerColor = bannerColor?.color
  const backgroundColor = !useImage && resolvedBannerColor ? resolvedBannerColor : undefined

  const message = (
    <LiveTextBanner
      title={deliveryOptions.title}
      subtitle={deliveryOptions.subtitle}
      titleTextColour={deliveryOptions.titleTextColour}
    />
  )

  const renderImage = () =>
    useImage && backgroundImage?.images?.length ? (
      <CmsImage
        queryParams={[
          {w: 768, fmt: 'auto', qlt: 'default'},
          {w: 1600, fmt: 'auto', qlt: 'default'},
        ]}
        className={styles.backgroundImage}
        images={backgroundImage.images}
        width="full"
        height="auto"
        imageRef={assetImageRef}
        onLoad={() => setLoadedAssetKey(selectedAssetKey)}
        onError={() => setLoadedAssetKey(selectedAssetKey)}
        style={{opacity: isBannerLoading ? 0 : 1}}
      />
    ) : null

  const renderLogo = () =>
    !useImage && logo?.images?.length ? (
      <Box className={styles.logoWrapper}>
        <CmsImage
          queryParams={[
            {w: 240, fmt: 'auto', qlt: 'default'},
            {w: 480, fmt: 'auto', qlt: 'default'},
          ]}
          className={styles.logoImage}
          images={logo.images}
          height="auto"
          imageRef={assetImageRef}
          onLoad={() => setLoadedAssetKey(selectedAssetKey)}
          onError={() => setLoadedAssetKey(selectedAssetKey)}
          style={{opacity: isBannerLoading ? 0 : 1}}
        />
      </Box>
    ) : null

  const banner = (
    <Box
        className={styles.bannerContainer}
        style={backgroundColor ? {backgroundColor} : undefined}
        aria-busy={isBannerLoading}
    >
      {isBannerLoading && (
        <Spinner color="primary" />
      )}
      {renderImage()}
      <Box
        className={styles.contentWrapper}
        style={{visibility: isBannerLoading ? 'hidden' : 'visible'}}
      >
        {renderLogo()}
        <Box
          className={styles.message}
          style={{'--delivery-title-offset': titleOffset} as React.CSSProperties}
        >
          {message}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box width="full" paddingX={['0px', '20px']} paddingTop={['0px', '24px']}>
      {deliveryOptions.url ? (
        <Link
          href={deliveryOptions.url}
          className={styles.bannerLink}
          onClickCapture={() => sendSelectPromotion(promotion)}
        >
          {banner}
        </Link>
      ) : (
        banner
      )}
    </Box>
  )
})
